import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { assetUrl } from "../utils/api";
import RecommendPeopleRail from "../components/RecommendPeopleRail";
import { GuestMatchHint } from "../components/WeChatLoginButton";
import LoveAssessGallery from "./LoveAssessGallery";
import LoveLanguagesSection from "./LoveLanguagesSection";
import LoveFruitSection from "./LoveFruitSection";
import AiModuleTabs from "../components/AiModuleTabs";
import {
  clearLoveMapProgress,
  completeLoveMapExpectation,
  completeLoveMapSelf,
  createLoveMapSession,
  expandLoveMapToFull,
  fetchLoveMapMatchDetail,
  fetchLoveMapMatches,
  fetchLoveMapMeta,
  fetchLoveMapResult,
  loadLoveMapProgress,
  requestLoveMapAiReport,
  saveLoveMapProgress,
  submitLoveMapAnswer,
} from "../api/loveMapApi";

const CHOICE_KEYS = [
  ["A", "option_a"],
  ["B", "option_b"],
  ["C", "option_c"],
  ["D", "option_d"],
];

function ProgressBar({ value }) {
  return (
    <div className="h-2 bg-white/80 rounded-full overflow-hidden border border-rose-100">
      <div
        className="h-full bg-gradient-to-r from-rose-400 via-fuchsia-500 to-violet-500 transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

function ChoiceCard({ card, selected, onSelect, busy, letters = CHOICE_KEYS }) {
  return (
    <div
      className="rounded-3xl shadow-xl border border-white/60 p-5 md:p-6 space-y-4 animate-[cardIn_0.35s_ease]"
      style={{ background: `linear-gradient(180deg, ${card.color || "#fff"} 0%, #ffffff 42%)` }}
    >
      <p className="text-xs font-medium text-gray-500">{card.category}</p>
      <h2 className="text-xl font-bold text-gray-800 leading-snug">{card.question}</h2>
      <div className="space-y-3 pt-1">
        {letters.map(([letter, key]) => {
          const text = card[key];
          if (!text) return null;
          const active = selected === letter;
          return (
            <button
              key={letter}
              type="button"
              disabled={busy}
              onClick={() => onSelect(letter)}
              className={`w-full text-left px-4 py-3.5 rounded-2xl border transition active:scale-[0.98] ${
                active
                  ? "border-rose-500 bg-rose-50 text-rose-800 shadow-sm"
                  : "border-gray-200/80 bg-white/90 hover:border-rose-300"
              }`}
            >
              <span className="font-semibold mr-2">{letter}.</span>
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TradeoffCard({ card, selected, onSelect, busy }) {
  return (
    <ChoiceCard
      card={card}
      selected={selected}
      onSelect={onSelect}
      busy={busy}
      letters={[
        ["A", "option_a"],
        ["B", "option_b"],
      ]}
    />
  );
}

function RankCard({ card, value, onChange, onConfirm, busy }) {
  const pool = card.options || [];
  const selected = value || [];

  const toggle = (trait) => {
    if (selected.includes(trait)) {
      onChange(selected.filter((t) => t !== trait));
      return;
    }
    if (selected.length >= 3) return;
    onChange([...selected, trait]);
  };

  const move = (idx, dir) => {
    const next = [...selected];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div
      className="rounded-3xl shadow-xl border border-white/60 p-5 md:p-6 space-y-4 animate-[cardIn_0.35s_ease]"
      style={{ background: `linear-gradient(180deg, ${card.color || "#E2E2FF"} 0%, #ffffff 40%)` }}
    >
      <p className="text-xs font-medium text-violet-600">排序卡 · 选 3 项并排优先级</p>
      <h2 className="text-xl font-bold text-gray-800 leading-snug">{card.question}</h2>
      <p className="text-xs text-gray-500">先点选最多 3 个特质，再用上下箭头调整顺序（①最重要）。</p>

      <div className="flex flex-wrap gap-2">
        {pool.map((trait) => {
          const on = selected.includes(trait);
          const rank = selected.indexOf(trait) + 1;
          return (
            <button
              key={trait}
              type="button"
              disabled={busy || (!on && selected.length >= 3)}
              onClick={() => toggle(trait)}
              className={`px-3 py-2 rounded-full text-sm border transition ${
                on
                  ? "bg-violet-500 text-white border-violet-500"
                  : "bg-white border-gray-200 text-gray-700 hover:border-violet-300"
              }`}
            >
              {on ? `${rank}. ` : ""}
              {trait}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="space-y-2 bg-violet-50/80 rounded-2xl p-3">
          <p className="text-xs text-violet-700 font-medium">你的排序</p>
          {selected.map((trait, idx) => (
            <div
              key={trait}
              className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-violet-100"
            >
              <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-xs flex items-center justify-center">
                {idx + 1}
              </span>
              <span className="flex-1 text-sm font-medium text-gray-800">{trait}</span>
              <button
                type="button"
                className="text-xs px-2 py-1 border rounded-lg disabled:opacity-30"
                disabled={idx === 0 || busy}
                onClick={() => move(idx, -1)}
              >
                上移
              </button>
              <button
                type="button"
                className="text-xs px-2 py-1 border rounded-lg disabled:opacity-30"
                disabled={idx === selected.length - 1 || busy}
                onClick={() => move(idx, 1)}
              >
                下移
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        disabled={busy || selected.length < 1}
        onClick={() => onConfirm(selected.slice(0, 3))}
        className="w-full py-3 rounded-full bg-violet-500 text-white font-semibold disabled:opacity-50"
      >
        确认这 {Math.min(3, selected.length) || 0} 项并继续
      </button>
    </div>
  );
}

function DualMapBars({ diffs = [] }) {
  if (!diffs.length) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-gray-500 px-1">
        <span className="text-rose-500 font-medium">● 我</span>
        <span>同维对照</span>
        <span className="text-violet-500 font-medium">● 理想 TA</span>
      </div>
      {diffs.map((d) => {
        const left = Math.min(d.me, d.ideal);
        const right = Math.max(d.me, d.ideal);
        const meLeft = d.me <= d.ideal;
        return (
          <div key={d.key} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">{d.label}</span>
              <span className="text-xs text-gray-400">差距 {d.gap}</span>
            </div>
            <div className="relative h-3 bg-white rounded-full border border-rose-100">
              <div
                className="absolute top-0 h-full rounded-full bg-gradient-to-r from-rose-200/80 to-violet-200/80"
                style={{ left: `${left}%`, width: `${Math.max(2, right - left)}%` }}
              />
              <span
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-rose-500 border-2 border-white shadow"
                style={{ left: `calc(${d.me}% - 6px)` }}
                title={`我 ${d.me}`}
              />
              <span
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-violet-500 border-2 border-white shadow"
                style={{ left: `calc(${d.ideal}% - 6px)` }}
                title={`理想 ${d.ideal}`}
              />
            </div>
            <div className="flex justify-between text-[11px] text-gray-500">
              <span className={meLeft ? "text-rose-600" : "text-violet-600"}>
                {meLeft ? `我 ${d.me}` : `理想 ${d.ideal}`}
              </span>
              <span className={!meLeft ? "text-rose-600" : "text-violet-600"}>
                {!meLeft ? `我 ${d.me}` : `理想 ${d.ideal}`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SideRadar({ radar = [], accent = "rose" }) {
  const bar =
    accent === "violet"
      ? "from-violet-400 to-fuchsia-500"
      : "from-rose-400 to-pink-500";
  return (
    <div className="space-y-2">
      {radar.map((item) => (
        <div key={item.key} className="flex items-center gap-2 text-sm">
          <span className="w-14 text-gray-500 shrink-0 text-xs">{item.label}</span>
          <div className="flex-1 h-2 bg-white rounded-full overflow-hidden border border-rose-50">
            <div
              className={`h-full bg-gradient-to-r ${bar} rounded-full transition-all duration-700`}
              style={{ width: `${Math.min(100, item.value || 0)}%` }}
            />
          </div>
          <span className="w-7 text-right text-xs font-medium text-gray-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function LoveMapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get("step") || "intro";
  const rawTab = searchParams.get("tab");
  const tab =
    rawTab ||
    (step && step !== "intro" ? "map" : "hub");

  useSeo({
    title:
      tab === "languages"
        ? "爱的五种语言测评_了解你的爱语 - AI婚恋平台"
        : tab === "fruit"
          ? "爱情水果_爱情性格测评 - AI婚恋平台"
          : tab === "map"
            ? "AI爱情地图_认识自己看见期待 - AI婚恋平台"
            : "爱情测评大厅_爱情地图五种语言水果 - AI婚恋平台",
    metaDescription:
      tab === "languages"
        ? "爱的五种语言原创测评：肯定的言辞、精心的时刻、接受礼物、服务的行动、身体的接触。"
        : tab === "fruit"
          ? "三分钟爱情水果性格测试，生成趣味研究报告，看看你是车厘子还是水蜜桃。"
          : "AI爱情地图、爱的五种语言、爱情水果：一套认识自己与亲密关系的趣味测评。",
    path:
      tab === "hub" || !rawTab
        ? "/love-map"
        : `/love-map?tab=${tab}`,
  });

  const [meta, setMeta] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [selfCards, setSelfCards] = useState([]);
  const [expCards, setExpCards] = useState([]);
  const [selfIndex, setSelfIndex] = useState(0);
  const [expIndex, setExpIndex] = useState(0);
  const [selfAnswers, setSelfAnswers] = useState({});
  const [expAnswers, setExpAnswers] = useState({});
  const [rankDraft, setRankDraft] = useState([]);
  const [animKey, setAnimKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [mapMode, setMapMode] = useState("full");
  const [aiBusy, setAiBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchNote, setMatchNote] = useState("");
  const [matchDetail, setMatchDetail] = useState(null);
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    fetchLoveMapMeta()
      .then(setMeta)
      .catch((err) => setError(err.message || "加载失败"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const saved = loadLoveMapProgress();
    if (!saved?.sessionId) return;
    setSessionId(saved.sessionId);
    setSelfCards(saved.selfCards || []);
    setExpCards(saved.expCards || []);
    setSelfIndex(saved.selfIndex || 0);
    setExpIndex(saved.expIndex || 0);
    setSelfAnswers(saved.selfAnswers || {});
    setExpAnswers(saved.expAnswers || {});
    if (saved.result) setResult(saved.result);
    if (saved.mode) setMapMode(saved.mode);
  }, []);

  useEffect(() => {
    if (step !== "result" || result || !sessionId) return;
    let cancelled = false;
    setBusy(true);
    fetchLoveMapResult(sessionId)
      .then((data) => {
        if (!cancelled) {
          setResult(data);
          saveLoveMapProgress({
            ...loadLoveMapProgress(),
            sessionId,
            result: data,
            stage: "result",
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "加载结果失败");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, sessionId, result]);

  const go = (next) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", "map");
    p.set("step", next);
    setSearchParams(p);
  };

  const switchTab = (nextTab) => {
    const p = new URLSearchParams();
    if (nextTab === "hub") {
      setSearchParams(p);
      return;
    }
    p.set("tab", nextTab);
    if (nextTab === "map") {
      p.set("step", "intro");
    }
    setSearchParams(p);
  };

  const persist = (patch = {}) => {
    saveLoveMapProgress({
      sessionId,
      mode: mapMode,
      selfCards,
      expCards,
      selfIndex,
      expIndex,
      selfAnswers,
      expAnswers,
      result,
      stage: step,
      ...patch,
    });
  };

  const startGame = async (mode = "full") => {
    setBusy(true);
    setError("");
    try {
      const data = await createLoveMapSession(mode);
      setSessionId(data.session_id);
      setMapMode(data.mode || mode);
      setSelfCards(data.self_cards || []);
      setExpCards(data.expectation_cards || []);
      setSelfIndex(0);
      setExpIndex(0);
      setSelfAnswers({});
      setExpAnswers({});
      setResult(null);
      setAnimKey((k) => k + 1);
      saveLoveMapProgress({
        sessionId: data.session_id,
        mode: data.mode || mode,
        selfCards: data.self_cards || [],
        expCards: data.expectation_cards || [],
        selfIndex: 0,
        expIndex: 0,
        selfAnswers: {},
        expAnswers: {},
        result: null,
        stage: "self",
      });
      go("self");
    } catch (err) {
      setError(err.message || "开始失败");
    } finally {
      setBusy(false);
    }
  };

  const continueToFull = async () => {
    if (!sessionId || busy) return;
    setBusy(true);
    setError("");
    try {
      const data = await expandLoveMapToFull(sessionId);
      setMapMode("full");
      setSelfCards(data.self_cards || []);
      setExpCards(data.expectation_cards || []);
      setSelfIndex(data.self_index || 0);
      setExpIndex(data.expectation_index || 0);
      setResult(null);
      const nextStage = data.stage === "expectation" ? "expectation" : "self";
      saveLoveMapProgress({
        sessionId: data.session_id,
        mode: "full",
        selfCards: data.self_cards || [],
        expCards: data.expectation_cards || [],
        selfIndex: data.self_index || 0,
        expIndex: data.expectation_index || 0,
        selfAnswers,
        expAnswers,
        result: null,
        stage: nextStage,
      });
      go(nextStage);
    } catch (err) {
      setError(err.message || "切换完整版失败");
    } finally {
      setBusy(false);
    }
  };

  const generateAiReport = async () => {
    if (!sessionId || aiBusy) return;
    setAiBusy(true);
    setError("");
    try {
      const data = await requestLoveMapAiReport(sessionId);
      const next = data.result || null;
      if (next) {
        setResult(next);
        setReportOpen(true);
        persist({ result: next, stage: "result" });
      }
    } catch (err) {
      setError(err.message || "生成报告失败");
    } finally {
      setAiBusy(false);
    }
  };

  const loadMatches = async (refresh = false) => {
    if (!userId) {
      setError("匹配需要登录。请微信登录后查看适合你的人；若只想自测，结果报告仍可游客查看。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await fetchLoveMapMatches({
        refresh,
        sessionId,
      });
      setMatches(data.items || []);
      setMatchNote(data.note || "");
      go("matches");
    } catch (err) {
      setError(err.message || "加载匹配失败");
    } finally {
      setBusy(false);
    }
  };

  const openMatchDetail = async (targetUserId) => {
    setBusy(true);
    setError("");
    try {
      const detail = await fetchLoveMapMatchDetail(targetUserId);
      setMatchDetail(detail);
    } catch (err) {
      setError(err.message || "加载详情失败");
    } finally {
      setBusy(false);
    }
  };

  const resumeGame = () => {
    const saved = loadLoveMapProgress();
    if (!saved?.sessionId) return;
    setSessionId(saved.sessionId);
    if (saved.mode) setMapMode(saved.mode);
    setSelfCards(saved.selfCards || []);
    setExpCards(saved.expCards || []);
    setSelfIndex(saved.selfIndex || 0);
    setExpIndex(saved.expIndex || 0);
    setSelfAnswers(saved.selfAnswers || {});
    setExpAnswers(saved.expAnswers || {});
    if (saved.result) {
      setResult(saved.result);
      if (saved.result.ai_report) setReportOpen(true);
    }
    if (saved.stage === "result" || saved.stage === "done") {
      go("result");
    } else if (saved.stage === "expectation") {
      go("expectation");
    } else {
      go("self");
    }
  };

  const currentSelf = selfCards[selfIndex];
  const currentExp = expCards[expIndex];

  const answerSelf = async (letter) => {
    if (!currentSelf || busy) return;
    setBusy(true);
    setError("");
    try {
      await submitLoveMapAnswer({
        sessionId,
        deck: "self",
        cardId: currentSelf.id,
        answer: letter,
        index: selfIndex,
      });
      const nextAnswers = { ...selfAnswers, [currentSelf.id]: letter };
      setSelfAnswers(nextAnswers);
      const nextIndex = selfIndex + 1;
      if (nextIndex >= selfCards.length) {
        await completeLoveMapSelf(sessionId);
        persist({ selfAnswers: nextAnswers, selfIndex: nextIndex, stage: "expectation" });
        setSelfIndex(nextIndex);
        go("expectation");
        setAnimKey((k) => k + 1);
      } else {
        setSelfIndex(nextIndex);
        persist({ selfAnswers: nextAnswers, selfIndex: nextIndex, stage: "self" });
        setAnimKey((k) => k + 1);
      }
    } catch (err) {
      setError(err.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const advanceExpectation = async (answerPayload) => {
    if (!currentExp || busy) return;
    setBusy(true);
    setError("");
    try {
      await submitLoveMapAnswer({
        sessionId,
        deck: "expectation",
        cardId: currentExp.id,
        answer: answerPayload,
        index: expIndex,
      });
      const nextAnswers = { ...expAnswers, [currentExp.id]: answerPayload };
      setExpAnswers(nextAnswers);
      setRankDraft([]);
      const nextIndex = expIndex + 1;
      if (nextIndex >= expCards.length) {
        const data = await completeLoveMapExpectation(sessionId);
        const mapResult = data.result || null;
        setResult(mapResult);
        persist({
          expAnswers: nextAnswers,
          expIndex: nextIndex,
          stage: "result",
          result: mapResult,
        });
        setExpIndex(nextIndex);
        go("result");
      } else {
        setExpIndex(nextIndex);
        persist({
          expAnswers: nextAnswers,
          expIndex: nextIndex,
          stage: "expectation",
        });
        setAnimKey((k) => k + 1);
      }
    } catch (err) {
      setError(err.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step === "expectation" && currentExp?.card_type === "rank") {
      const prev = expAnswers[currentExp.id];
      setRankDraft(Array.isArray(prev) ? prev : []);
    }
  }, [step, expIndex, currentExp?.id]);

  const selfProgress = selfCards.length
    ? Math.round((Object.keys(selfAnswers).length / selfCards.length) * 100)
    : 0;
  const expProgress = expCards.length
    ? Math.round((Object.keys(expAnswers).length / expCards.length) * 100)
    : 0;

  const saved = useMemo(() => loadLoveMapProgress(), [step, sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-violet-50 to-sky-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8 md:py-10">
        <div className="max-w-6xl mx-auto flex gap-6 items-start justify-center">
          <div
            className={`w-full space-y-5 ${
              tab === "hub" ? "max-w-3xl" : "max-w-lg"
            }`}
          >
          <AiModuleTabs groupKey="relationship" />
          {tab !== "hub" && (
            <button
              type="button"
              onClick={() => switchTab("hub")}
              className="text-sm text-gray-500 hover:text-rose-600"
            >
              ← 返回测评大厅
            </button>
          )}

          {tab === "hub" ? (
            <>
              <div className="lg:hidden">
                <RecommendPeopleRail layout="strip" />
              </div>
              <GuestMatchHint redirect="/love-map" />
              <LoveAssessGallery onSelect={switchTab} />
            </>
          ) : tab === "languages" ? (
            <LoveLanguagesSection />
          ) : tab === "fruit" ? (
            <LoveFruitSection />
          ) : (
            <>
          <div className="lg:hidden">
            <RecommendPeopleRail layout="strip" />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {step === "intro" && (
            <section className="space-y-8 animate-[fadeIn_0.35s_ease]">
              <header className="text-center space-y-4">
                <div className="mx-auto w-40 h-52 rounded-2xl bg-gradient-to-br from-rose-400 via-fuchsia-500 to-violet-500 shadow-xl shadow-rose-200 flex flex-col items-center justify-center text-white -rotate-2 hover:rotate-0 transition-transform duration-500">
                  <span className="text-4xl mb-2">❤️</span>
                  <p className="font-bold tracking-wide">AI爱情地图</p>
                </div>
                <h1 className="text-3xl font-extrabold text-gray-800">AI爱情地图</h1>
                <p className="text-rose-600 font-medium">
                  {meta?.subtitle || "认识自己，看见期待，找到适合你的TA。"}
                </p>
                <p className="text-sm text-gray-500">
                  {meta?.tagline || "不是测试你是谁，而是帮你看清自己的爱情。"}
                </p>
              </header>

              <section className="bg-white/90 rounded-3xl border border-rose-100 shadow-sm p-6 space-y-4 text-sm text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-800">你真的了解自己吗？</p>
                <p>你知道自己喜欢什么样的人，但你是否知道：你真正需要什么样的关系？</p>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">
                      {loading
                        ? "…"
                        : meta?.modes?.quick?.total ??
                          (meta?.modes?.quick
                            ? meta.modes.quick.self_count + meta.modes.quick.expectation_count
                            : 40)}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">快速版题量</p>
                    <p className="text-[11px] text-amber-600 mt-1">
                      {meta?.modes?.quick?.minutes || "约 3～5 分钟"}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4 text-center">
                    <p className="text-2xl font-bold text-violet-700">
                      {loading
                        ? "…"
                        : meta?.modes?.full?.total ??
                          ((meta?.self_count ?? 40) + (meta?.expectation_count ?? 40))}
                    </p>
                    <p className="text-xs text-violet-800 mt-1">完整版题量</p>
                    <p className="text-[11px] text-violet-600 mt-1">
                      {meta?.modes?.full?.minutes || "约 8～12 分钟"}
                    </p>
                  </div>
                </div>
                <p>
                  建议先做快速版生成基础地图；若想更深入，可再继续完整版。
                  <span className="text-gray-800 font-medium">自己测一测</span>
                  可游客完成；
                  <span className="text-rose-600 font-medium">想匹配对象</span>
                  请微信登录后再查看推荐。
                </p>
                <GuestMatchHint redirect="/love-map?tab=map" />
                {meta?.disclaimer && (
                  <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                    {meta.disclaimer}
                  </p>
                )}
              </section>

              <div className="space-y-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => startGame("quick")}
                  className="w-full py-3.5 rounded-full bg-rose-500 text-white font-semibold shadow-lg shadow-rose-200 hover:bg-rose-600 disabled:opacity-60"
                >
                  {busy ? "准备卡牌中..." : "快速版（约20+20题 · 3～5分钟）"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => startGame("full")}
                  className="w-full py-3 rounded-full border border-violet-300 text-violet-700 font-semibold bg-white hover:bg-violet-50 disabled:opacity-60"
                >
                  完整版（全部题库 · 更深入）
                </button>
                {saved?.sessionId &&
                  saved.stage !== "intro" &&
                  (saved.stage === "result" || saved.result ? (
                    <button
                      type="button"
                      onClick={resumeGame}
                      className="w-full py-2.5 text-sm text-violet-600"
                    >
                      查看我的爱情地图
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={resumeGame}
                      className="w-full py-2.5 text-sm text-violet-600"
                    >
                      继续未完成的地图
                    </button>
                  ))}
              </div>
            </section>
          )}

          {step === "self" && currentSelf && (
            <section className="space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <button type="button" onClick={() => go("intro")} className="hover:text-rose-600">
                  退出
                </button>
                <span>
                  自我认知 · 第 {selfIndex + 1} / {selfCards.length} 张
                </span>
                <button
                  type="button"
                  disabled={selfIndex === 0 || busy}
                  onClick={() => {
                    setSelfIndex((i) => Math.max(0, i - 1));
                    setAnimKey((k) => k + 1);
                  }}
                  className="disabled:opacity-30 hover:text-rose-600"
                >
                  上一张
                </button>
              </div>
              <ProgressBar value={selfProgress} />
              <p className="text-center text-xs text-amber-700/80">阶段一：我是谁？ · 没有标准答案</p>
              <div key={`self-${animKey}`}>
                <ChoiceCard
                  card={currentSelf}
                  selected={selfAnswers[currentSelf.id]}
                  onSelect={answerSelf}
                  busy={busy}
                />
              </div>
            </section>
          )}

          {step === "expectation" && currentExp && (
            <section className="space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <button type="button" onClick={() => go("intro")} className="hover:text-violet-600">
                  退出
                </button>
                <span>
                  择偶期望 · 第 {expIndex + 1} / {expCards.length} 张
                </span>
                <button
                  type="button"
                  disabled={expIndex === 0 || busy}
                  onClick={() => {
                    setExpIndex((i) => Math.max(0, i - 1));
                    setAnimKey((k) => k + 1);
                    setRankDraft([]);
                  }}
                  className="disabled:opacity-30 hover:text-violet-600"
                >
                  上一张
                </button>
              </div>
              <ProgressBar value={expProgress} />
              <p className="text-center text-xs text-violet-700/80">阶段二：我想要谁？</p>
              <div key={`exp-${animKey}`}>
                {currentExp.card_type === "tradeoff" ? (
                  <TradeoffCard
                    card={currentExp}
                    selected={expAnswers[currentExp.id]}
                    onSelect={(letter) => advanceExpectation(letter)}
                    busy={busy}
                  />
                ) : currentExp.card_type === "rank" ? (
                  <RankCard
                    card={currentExp}
                    value={rankDraft}
                    onChange={setRankDraft}
                    onConfirm={(ranked) => advanceExpectation(ranked)}
                    busy={busy}
                  />
                ) : (
                  <ChoiceCard
                    card={currentExp}
                    selected={expAnswers[currentExp.id]}
                    onSelect={(letter) => advanceExpectation(letter)}
                    busy={busy}
                  />
                )}
              </div>
            </section>
          )}

          {step === "result" && (
            <section className="space-y-5 animate-[fadeIn_0.35s_ease]">
              {!result ? (
                <div className="bg-white rounded-3xl border border-rose-100 p-8 text-center text-gray-500">
                  {busy ? "正在生成你的爱情地图…" : "结果加载中…"}
                </div>
              ) : (
                <>
                  <header className="text-center space-y-2">
                    <p className="text-sm text-rose-500 font-medium">阶段三 · AI爱情地图</p>
                    <h2 className="text-2xl font-extrabold text-gray-800">
                      {mapMode === "quick" ? "我的爱情地图" : "我的完整爱情地图"}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {mapMode === "quick"
                        ? "快速版结果。若想更深入，可继续完成完整版。"
                        : "差异不是问题，适合才是关键"}
                    </p>
                    <div className="inline-flex items-baseline gap-1 bg-white/90 border border-rose-100 rounded-full px-4 py-1.5 mt-1">
                      <span className="text-xs text-gray-500">契合度</span>
                      <span className="text-2xl font-bold text-rose-500">
                        {result.alignment_score}
                      </span>
                    </div>
                  </header>

                  {mapMode === "quick" && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={continueToFull}
                      className="w-full py-3 rounded-full bg-violet-600 text-white font-semibold disabled:opacity-60"
                    >
                      {busy ? "准备中..." : "继续完善完整版（剩余题目）"}
                    </button>
                  )}

                  <div className="bg-white/95 rounded-3xl border border-rose-100 shadow-sm p-5 space-y-3">
                    <h3 className="font-bold text-gray-800">爱情地图 · 同维连线</h3>
                    <DualMapBars diffs={result.diffs || []} />
                  </div>

                  <div className="grid gap-4">
                    <div className="bg-rose-50/80 rounded-3xl border border-rose-100 p-5 space-y-3">
                      <p className="text-xs font-medium text-rose-600">我是谁</p>
                      <h3 className="text-lg font-bold text-gray-800">
                        {result.self_profile?.personality_label || "我的画像"}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {result.self_profile?.one_liner}
                      </p>
                      <SideRadar radar={result.self_profile?.radar || []} accent="rose" />
                      {result.self_profile?.ai_needs && (
                        <p className="text-xs text-gray-500 border-t border-rose-100 pt-3">
                          你可能更需要：{result.self_profile.ai_needs}
                        </p>
                      )}
                    </div>

                    <div className="bg-violet-50/80 rounded-3xl border border-violet-100 p-5 space-y-3">
                      <p className="text-xs font-medium text-violet-600">我想要谁</p>
                      <h3 className="text-lg font-bold text-gray-800">理想伴侣侧写</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {result.ideal_profile?.one_liner}
                      </p>
                      <SideRadar radar={result.ideal_profile?.radar || []} accent="violet" />
                      {(result.ideal_profile?.top_traits || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {result.ideal_profile.top_traits.slice(0, 8).map((t) => (
                            <span
                              key={t}
                              className="text-xs px-2.5 py-1 rounded-full bg-white border border-violet-200 text-violet-700"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {(result.ai_fit_notes || result.ai_complement_notes) && (
                    <div className="bg-white rounded-3xl border border-rose-100 p-5 space-y-3 text-sm text-gray-600 leading-relaxed">
                      {result.ai_fit_notes && (
                        <div>
                          <p className="font-semibold text-gray-800 mb-1">契合之处</p>
                          <p>{result.ai_fit_notes}</p>
                        </div>
                      )}
                      {result.ai_complement_notes && (
                        <div>
                          <p className="font-semibold text-gray-800 mb-1">差异与互补</p>
                          <p>{result.ai_complement_notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-white/90 rounded-2xl border border-amber-100 p-4 space-y-2">
                    <p className="text-xs font-medium text-amber-700">各维说明</p>
                    <ul className="space-y-2 text-xs text-gray-500">
                      {(result.diffs || [])
                        .filter((d) => d.gap >= 15)
                        .slice(0, 4)
                        .map((d) => (
                          <li key={d.key}>· {d.note}</li>
                        ))}
                      {(result.diffs || []).every((d) => d.gap < 15) && (
                        <li>· 各维差距都不大，你与理想期待整体较接近。</li>
                      )}
                    </ul>
                  </div>

                  {result.disclaimer && (
                    <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                      {result.disclaimer}
                    </p>
                  )}

                  {(reportOpen || result.ai_report?.source === "llm") && result.ai_report && (
                    <div className="bg-gradient-to-br from-white to-rose-50/60 rounded-3xl border border-rose-100 p-5 space-y-4 animate-[fadeIn_0.35s_ease]">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-bold text-gray-800">AI 深度报告</h3>
                        <span className="text-[11px] text-gray-400">
                          {result.ai_report.source === "llm" ? "AI 润色" : "规则解读"}
                        </span>
                      </div>
                      <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
                        <div>
                          <p className="text-xs font-semibold text-rose-600 mb-1">我是谁</p>
                          <p>{result.ai_report.who_i_am}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-rose-600 mb-1">真正需要什么</p>
                          <p>{result.ai_report.what_i_need}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-violet-600 mb-1">想要谁</p>
                          <p>{result.ai_report.who_i_want}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-amber-700 mb-1">可能的盲区</p>
                          <p>{result.ai_report.blind_spots}</p>
                        </div>
                        {(result.ai_report.partner_types || []).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              可能适合的伴侣类型
                            </p>
                            <ul className="space-y-1.5">
                              {result.ai_report.partner_types.map((t) => (
                                <li key={t} className="text-sm">
                                  · {t}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(result.ai_report.relationship_tips || []).length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2">相处提示</p>
                            <ul className="space-y-1.5">
                              {result.ai_report.relationship_tips.map((t) => (
                                <li key={t} className="text-sm">
                                  · {t}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400">
                        以上为兴趣向自我探索，不是心理诊断；用「可能适合」理解即可。
                      </p>
                    </div>
                  )}

                  <div className="space-y-3 pt-1">
                    <button
                      type="button"
                      disabled={aiBusy || !sessionId}
                      onClick={generateAiReport}
                      className="w-full py-3.5 rounded-full bg-rose-500 text-white font-semibold shadow-lg shadow-rose-200 hover:bg-rose-600 disabled:opacity-60"
                    >
                      {aiBusy
                        ? "正在生成 AI 报告…"
                        : reportOpen
                          ? "刷新 AI 深度报告"
                          : "生成 AI 深度报告"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => loadMatches(true)}
                      className="w-full py-3.5 rounded-full bg-violet-500 text-white font-semibold hover:bg-violet-600 disabled:opacity-60"
                    >
                      {userId ? "看看适合我的人" : "微信登录后匹配对象"}
                    </button>
                    {!userId && (
                      <GuestMatchHint redirect="/love-map?tab=map&step=result" />
                    )}
                    <Link
                      to="/love-map/couple"
                      className="block w-full py-3 rounded-full border border-violet-200 text-center text-sm text-violet-700 font-medium"
                    >
                      双人爱情地图 · 情侣关系
                    </Link>
                    <Link
                      to="/ai/relationship-map"
                      className="block w-full py-3 rounded-full border border-rose-200 text-center text-sm text-rose-700 font-medium"
                    >
                      查看统一 AI 关系地图（五层）
                    </Link>
                    <Link
                      to="/discover"
                      className="block w-full py-3 rounded-full border text-center text-sm text-gray-700"
                    >
                      去寻找对象
                    </Link>                    <button
                      type="button"
                      onClick={() => {
                        clearLoveMapProgress();
                        setSessionId(null);
                        setResult(null);
                        setReportOpen(false);
                        setMatches([]);
                        setMatchDetail(null);
                        go("intro");
                      }}
                      className="w-full py-2 text-sm text-gray-500"
                    >
                      再玩一次
                    </button>
                  </div>
                </>
              )}
            </section>
          )}

          {step === "matches" && (
            <section className="space-y-4 animate-[fadeIn_0.35s_ease]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">可能适合你的人</h2>
                <button
                  type="button"
                  onClick={() => go("result")}
                  className="text-sm text-rose-600"
                >
                  返回地图
                </button>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                {matchNote ||
                  "匹配基于爱情地图画像，仅供参考，不代表一定适合。差异不是问题。"}
              </p>
              {matches.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm space-y-3">
                  <p>暂时没有可对比的用户画像</p>
                  <p>当更多会员完成爱情地图后，这里会出现推荐</p>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => loadMatches(true)}
                    className="text-violet-600"
                  >
                    刷新试试
                  </button>
                  <Link to="/discover" className="text-rose-600 inline-block">
                    先去寻找对象 →
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {matches.map((item) => (
                    <div
                      key={item.id || item.target_user_id}
                      className="bg-white rounded-2xl border border-rose-100 p-4 flex gap-3 items-center"
                    >
                      <img
                        src={
                          item.user?.avatar
                            ? assetUrl(item.user.avatar)
                            : "https://via.placeholder.com/56"
                        }
                        alt=""
                        className="w-14 h-14 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {item.user?.name || "会员"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.user?.age || "?"}岁 · {item.user?.city || "城市未填"}
                          {item.their_label ? ` · ${item.their_label}` : ""}
                        </p>
                        <p className="text-sm text-violet-600 font-medium mt-0.5">
                          契合参考 {item.score}%
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => openMatchDetail(item.target_user_id)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-violet-500 text-white"
                        >
                          为什么可能适合
                        </button>
                        <Link
                          to={`/user/${item.target_user_id}`}
                          className="text-xs px-3 py-1.5 rounded-lg border text-center"
                        >
                          看资料
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {matchDetail && (
                <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4">
                  <div className="bg-white rounded-3xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 space-y-4 shadow-xl">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg">
                          {matchDetail.user?.name || "会员"}
                        </h3>
                        <p className="text-sm text-violet-600 font-medium">
                          契合参考 {matchDetail.score}%
                        </p>
                        {(matchDetail.my_label || matchDetail.their_label) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {matchDetail.my_label || "我"} ·{" "}
                            {matchDetail.their_label || "TA"}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setMatchDetail(null)}
                        className="text-gray-400 text-sm"
                      >
                        关闭
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">{matchDetail.ai_reason}</p>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-700">可能契合的地方</p>
                      {(matchDetail.reasons || []).map((r, i) => (
                        <div
                          key={i}
                          className="text-sm bg-rose-50 rounded-xl px-3 py-2 text-gray-700"
                        >
                          <span className="font-medium text-rose-600">
                            {r.dimension}
                          </span>
                          ：{r.text}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-700">需要留意</p>
                      <ul className="space-y-1.5 text-sm text-gray-600">
                        {(matchDetail.challenges || []).map((c, i) => (
                          <li key={i}>· {c}</li>
                        ))}
                      </ul>
                    </div>
                    {(matchDetail.radar_compare || []).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-700">八维对照</p>
                        {matchDetail.radar_compare.map((d) => (
                          <div key={d.key} className="text-xs flex justify-between gap-2">
                            <span className="text-gray-500 w-14">{d.label}</span>
                            <span className="text-rose-600">我 {d.me}</span>
                            <span className="text-violet-600">TA {d.them}</span>
                            <span className="text-gray-400">差 {d.gap}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <Link
                      to={`/user/${matchDetail.target_user_id}`}
                      className="block w-full py-3 rounded-full bg-rose-500 text-white text-center font-semibold"
                    >
                      查看完整资料
                    </Link>
                    <Link
                      to={`/love-map/couple?partner=${matchDetail.target_user_id}`}
                      className="block w-full py-3 rounded-full border border-violet-200 text-violet-700 text-center text-sm font-medium"
                    >
                      邀请生成双人地图
                    </Link>
                  </div>
                </div>
              )}
            </section>
          )}
            </>
          )}
          </div>

          <div className="hidden lg:block shrink-0">
            <RecommendPeopleRail layout="sidebar" />
          </div>
        </div>
      </main>
      <Footer />
      <style>{`
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(14px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export function AiLoveCardRedirect() {
  return <Navigate to="/love-map" replace />;
}
